import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  IGroupsItem,
  getBurn,
  getBurnSettings,
  getDisplaySettings,
  getFinanceInfo,
  getHomeInsights,
  getScheduledInvoiceMappings,
  getTotalBalances,
  markInsightsAsViewed,
  updateBurnSettings,
  updateDisplaySettings,
} from '~apis/dashboard';
import { TaxInvoiceType } from '~models/tax-invoice';

import { BankAccountCashFlowsQueryKey } from '../bank-accounts/cashFlows/query';
import { HomeQueryKey } from '../home/query';

interface CompanyIdentifier {
  companyId: string;
}

export const DashboardQueryKey = {
  default: () => ['dashboard'],
  list: ({ companyId }: CompanyIdentifier) => [...DashboardQueryKey.default(), 'list', companyId],
  displaySettings: ({ companyId }: CompanyIdentifier) => [
    ...DashboardQueryKey.default(),
    'displaySettings',
    companyId,
  ],
  burn: ({ companyId }: CompanyIdentifier) => [...DashboardQueryKey.default(), 'burn', companyId],
  burnSettings: ({ companyId }: CompanyIdentifier) => [
    ...DashboardQueryKey.default(),
    'burnSettings',
    companyId,
  ],
  scheduledInvoiceMapping: ({ companyId, type }: { companyId: string; type: TaxInvoiceType }) => [
    ...DashboardQueryKey.default(),
    'scheduledInvoiceMapping',
    companyId,
    type,
  ],
  financeInfo: ({ companyId }: { companyId: string }) => [
    ...DashboardQueryKey.default(),
    'financeInfo',
    companyId,
  ],
  insights: ({ companyId, from, to }: { companyId: string; from: string; to: string }) => [
    ...DashboardQueryKey.default(),
    'insights',
    companyId,
    from,
    to,
  ],
};

export function useGetTotalBalances(identifier: CompanyIdentifier) {
  return useQuery({
    queryKey: DashboardQueryKey.list(identifier),
    queryFn: () => getTotalBalances(identifier),
    enabled: !!identifier.companyId,
  });
}

export function useGetDisplaySettings(identifier: CompanyIdentifier) {
  return useQuery({
    queryKey: DashboardQueryKey.displaySettings(identifier),
    queryFn: () => getDisplaySettings(identifier),
    enabled: !!identifier.companyId,
  });
}

export function useGetBurn(identifier: CompanyIdentifier) {
  return useQuery({
    queryKey: DashboardQueryKey.burn(identifier),
    queryFn: () => getBurn(identifier),
    enabled: !!identifier.companyId,
  });
}

export function useUpdateDisplaySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyId: string; groups: IGroupsItem[] }) => updateDisplaySettings(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(
        DashboardQueryKey.displaySettings({ companyId: variables.companyId }),
      );
      queryClient.invalidateQueries(DashboardQueryKey.list({ companyId: variables.companyId }));
      queryClient.invalidateQueries(
        BankAccountCashFlowsQueryKey.default({ companyId: variables.companyId }),
      );
      queryClient.invalidateQueries(HomeQueryKey.dashboard({ companyId: variables.companyId }));
      // also refresh burn when display settings change
      queryClient.invalidateQueries(DashboardQueryKey.burn({ companyId: variables.companyId }));
    },
  });
}

export function useGetScheduledInvoiceMapping({
  companyId,
  type,
}: {
  companyId: string;
  type: TaxInvoiceType;
}) {
  return useQuery({
    queryKey: DashboardQueryKey.scheduledInvoiceMapping({
      companyId,
      type,
    }),
    queryFn: () =>
      getScheduledInvoiceMappings({
        companyId,
        type,
      }),
    enabled: !!companyId,
  });
}

export function useGetBurnSettings(identifier: CompanyIdentifier) {
  return useQuery({
    queryKey: DashboardQueryKey.burnSettings(identifier),
    queryFn: () => getBurnSettings(identifier),
    enabled: !!identifier.companyId,
  });
}

export function useUpdateBurnSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      companyId: string;
      request: {
        months: number | null;
        baseDate: string | null;
        categories: number[];
        excludeHiddenAccounts: boolean;
      };
    }) => updateBurnSettings(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(
        DashboardQueryKey.burnSettings({ companyId: variables.companyId }),
      );
      queryClient.invalidateQueries(DashboardQueryKey.burn({ companyId: variables.companyId }));
    },
  });
}

export function useGetFinanceInfo({
  companyId,
  checkPermission,
  permissionEnabled,
}: {
  companyId: string;
  checkPermission?: boolean;
  permissionEnabled?: boolean;
}) {
  return useQuery({
    queryKey: DashboardQueryKey.default(),
    queryFn: () => getFinanceInfo({ companyId }),
    enabled: checkPermission ? !!companyId && permissionEnabled : !!companyId,
  });
}

export function useGetHomeInsights({
  companyId,
  from,
  to,
}: {
  companyId: string;
  from: string;
  to: string;
}) {
  return useQuery({
    queryKey: DashboardQueryKey.insights({ companyId, from, to }),
    queryFn: () => getHomeInsights({ companyId, from, to }),
    enabled: !!companyId && !!from && !!to,
  });
}

export function useMarkInsightsAsViewed({ companyId }: { companyId: string }) {
  return useMutation({
    mutationFn: (insightKeys: string[]) => markInsightsAsViewed({ companyId, insightKeys }),
  });
}
